import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import ClientProfileFields from "../../../../../components/ProfileDialog/components/ClientProfileFields";
import CloseIcon from "@mui/icons-material/Close";
import { enqueueSnackbar } from "notistack";
import { useIds } from "../../../../../hook/AdminContext";
import { upload_image_documents_on_s3 } from "../../../../../DAL/jobs/jobs";
import { add_coach, edit_coach } from "../../../../../DAL/Coach/coach";
import { useParams } from "react-router-dom";

const AddEditCoachDialog = ({
  open,
  onClose,
  editData = null, // if not null, update mode
  handleCallBackFunction = () => {},
}) => {
  const { userInfo } = useIds();
  const { source_slug, type_slug, feature_slug } = useParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputs, setInputs] = useState({
    name: "",
    company_name: "",
    email: "",
    contact_name: "",
    contact_number: "",
    image: "",
    profile_type_id: [],
    website_url: "",
    instagram_link: "",
    facebook_link: "",
    twitter_link: "",
    linkedIn_link: "",
    youtube: "",
  });
  const [croppedImage, setCroppedImage] = useState(null);

  const isUpdateMode = Boolean(editData);

  useEffect(() => {
    if (isUpdateMode && editData) {
      setInputs({
        ...editData,
        company_name:
          userInfo?.company_profile?.company_name ||
          userInfo?.company_name ||
          "",
        about: editData.description,
        contact_name: editData.name,
        contact_number: editData.contact_number,
        image: editData.profile_image?.thumbnail_1 || "",
        profile_type_id: Array.isArray(editData?.profile_type)
          ? editData?.profile_type.map((item) => item._id) // case 1: if profile type is array
          : editData?.profile_type?._id
          ? [editData.profile_type._id] // case 2: if profile type is a single object
          : [],
        website_url: editData.website_url,
        instagram_link: editData.social_links?.instagram || "",
        facebook_link: editData.social_links?.facebook || "",
        twitter_link: editData.social_links?.twitter || "",
        linkedIn_link: editData.social_links?.linkedin || "",
        youtube: editData.social_links?.youtube || "",
      });
    } else {
      setInputs({
        name: "",
        email: "",
        company_name:
          userInfo?.company_profile?.company_name ||
          userInfo?.company_name ||
          "",
        contact_name: "",
        contact_number: "",
        image: "",
        profile_type_id: [],
        website_url: "",
        instagram_link: "",
        facebook_link: "",
        twitter_link: "",
        linkedIn_link: "",
        youtube: "",
      });
    }
    setCroppedImage(null);
  }, [editData, isUpdateMode]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!inputs.about?.trim()) {
      enqueueSnackbar(`About Coach is required`, { variant: "error" });
      return;
    }
    if (!inputs.image) {
      enqueueSnackbar(`Profile Image is required`, { variant: "error" });
      return;
    }

    if (inputs.profile_type_id.length == 0) {
      enqueueSnackbar("Type is required.", { variant: "error" });
      return;
    }

    handleSubmitAddEditCoach({ ...inputs, croppedImage });
  };

  const handleSubmitAddEditCoach = async (data) => {
    setIsSubmitting(true);
    let imageURL = data.image;
    if (imageURL instanceof File) {
      const formData = new FormData();
      formData.append("image", imageURL);
      try {
        const res = await upload_image_documents_on_s3(formData);
        if (res.code === 200) {
          imageURL = res.image_path;
        }
      } catch (err) {
        enqueueSnackbar(`Failed to upload ${imageURL.name}`, {
          variant: "error",
        });
        setIsSubmitting(false);
        return;
      }
    }

    const postData = {
      name: data.contact_name || "",
      image: imageURL || "",
      email: data.email,
      company_name: data.company_name || "",
      type: data.profile_type_id || [],
      short_summary: data.short_summary || "",
      source_slug: source_slug,
      social_links: {
        facebook: data.facebook_link || "",
        instagram: data.instagram_link || "",
        linkedin: data.linkedIn_link || "",
        twitter: data.twitter_link || "",
        youtube: data.youtube || "",
      },
      description: data.about || "",
      website_url: data.website_url || "",
      location: data.location || "",
      contact_number: data.contact_number || "",
    };

    if (!editData && userInfo.isCompanyProfile) {
      postData.action_id = userInfo._id;
    }

    const resp = editData
      ? await edit_coach(editData._id, postData)
      : await add_coach(postData);
    if (resp.code == 200) {
      handleCallBackFunction();
      onClose(false);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ component: "form", onSubmit: handleSubmit }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>{isUpdateMode ? "Update Coach" : "Add New Coach"}</div>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <ClientProfileFields
          inputs={inputs}
          croppedImage={croppedImage}
          setCroppedImage={setCroppedImage}
          setInputs={setInputs}
          isEditForm={isUpdateMode}
          isCompanyProfile={false}
          isAllowSectorFlow={false}
          isTypeAndShortSummaryAllow={true}
          feature_slug={feature_slug}
          isCoachFields={true}
        />
      </DialogContent>

      <DialogActions className="px-4 py-3">
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          disabled={isSubmitting}
          type="submit"
          variant="contained"
          color="primary"
          className="btn-contained"
        >
          {isSubmitting
            ? "Submitting..."
            : isUpdateMode
            ? "Update Coach"
            : "Add Coach"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditCoachDialog;
